import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Loading, ErrorNote, confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';

interface Criterion {
    id: number;
    name: string;
    description: string | null;
    weight: string | number;
    displayOrder: number;
    _count?: { questions: number };
}

const EMPTY = { id: undefined as number | undefined, name: '', description: '', weight: '1', displayOrder: '0' };

export function Criteria() {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState<ApiError | null>(null);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['criteria'],
        queryFn: () => api.get<Criterion[]>('/api/admin/criteria')
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/criteria', {
            ...form,
            weight: Number(form.weight),
            displayOrder: Number(form.displayOrder)
        }),
        onSuccess: async () => {
            toast('Criterion saved.');
            setForm(EMPTY);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['criteria'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/criteria/${id}/delete`),
        onSuccess: async () => {
            toast('Criterion retired.');
            await queryClient.invalidateQueries({ queryKey: ['criteria'] });
        },
        // The server refuses while questions still reference it, and says how many. That message is
        // more useful than anything this page could invent.
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const onDelete = async (criterion: Criterion) => {
        const confirmed = await confirmAction({
            title: `Remove ${criterion.name}?`,
            confirmText: 'Retire criterion',
            danger: true
        });

        if (confirmed) remove.mutate(criterion.id);
    };

    return (
        // Stacked, matching Interview formats. The two-column grid gave the editor a 22rem sidebar it
        // did not fit in and left the table a horizontally scrolling strip.
        <div className="space-y-6">
            <Card
                heading
                title="What we score"
                lede="The dimensions every answer is scored on. Interview formats pick which of these apply."
            >
                {isLoading ? <Loading /> : (
                    <DataTable
                        rows={data ?? []}
                        rowKey={(row) => row.id}
                        empty="No criteria yet. Add one on the right."
                        columns={[
                            { header: 'Name', sortBy: (row) => row.name, cell: (row) => <span className="font-medium">{row.name}</span> },
                            { header: 'Description', sortBy: (row) => row.description, cell: (row) => row.description ?? '—' },
                            { header: 'Weight', sortBy: (row) => Number(row.weight), cell: (row) => Number(row.weight) },
                            { header: 'Questions', sortBy: (row) => row._count?.questions ?? 0, cell: (row) => row._count?.questions ?? 0 },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setForm({
                                                id: row.id,
                                                name: row.name,
                                                description: row.description ?? '',
                                                weight: String(Number(row.weight)),
                                                displayOrder: String(row.displayOrder)
                                            })}
                                        >
                                            Edit
                                        </Button>
                                        <Button variant="secondary" onClick={() => onDelete(row)}>
                                            Retire
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            <Card title={form.id ? 'Edit criterion' : 'Add a criterion'}>
                <form
                    className="space-y-4"
                    onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
                >
                    {error && !error.errors && <ErrorNote message={error.message} />}

                    <Field label="Name" error={error?.fieldError('name')}>
                        <TextInput
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            invalid={!!error?.fieldError('name')}
                        />
                    </Field>

                    <Field label="Description">
                        <TextArea
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                    </Field>

                    <Field
                        label="Weight"
                        error={error?.fieldError('weight')}
                        hint="Relative importance. Weights need not add up to anything."
                    >
                        <TextInput
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.weight}
                            onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
                        />
                    </Field>

                    <Field label="Order shown" hint="Lower numbers appear first.">
                        <TextInput
                            type="number"
                            min="0"
                            value={form.displayOrder}
                            onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
                        />
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={save.isPending}>Save</Button>
                        {form.id && (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => { setForm(EMPTY); setError(null); }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </form>
            </Card>
        </div>
    );
}
